import { css } from 'styled-components';
import { darken, lighten } from 'polished';
import { blue, green, orange, red } from './variables';

export default css`
  --text: #6c757d;
  --body-bg: #fafbfe;

  --bg: #ffffff;
  --bg-1: #f1f3fa;
  --bg-2: #eef2f7;
  --bg-3: #dee2e6;
  --bg-4: #ced4da;
  --bg-5: #adb5bd;
  --bg-6: #98a6ad;
  --bg-7: #88959b;

  --skeleton-color: var(--bg-2);
  --skeleton-sheen-color: var(--bg-1);

  --primary: ${red};
  --secondary: ${darken(0.15, red)};
  --red: ${red};
  --error: ${red};

  // Layout.
  --logo-color: var(--red);
  --box-shadow: 0 0 35px 0 rgb(154 161 171 / 15%);
  --box-shadow-component: 0 2px 6px 0 rgb(154 161 171 / 50%);
  --menu-bg: #313a46;
  --menu-text: #aeb9c6;
  --menu-text-active: #ffffff;
  --menu-active-bg: #2c343f;
  --menu-logo-color: #ffffff;
  --menu-border-color: #8391a236;
  --menu-category-color: #98a0aa;

  // Type.
  --title-text: #313a46;
  --text-strong: inherit;
  --text-muted: #868e96;

  // Navbar.
  --navbar-button-bg: #fafbfd;
  --navbar-button-border-color: var(--bg-1);
  --navbar-button-text: var(--bg-6);
  --navbar-icon-color: var(--bg-6);
  --navbar-menu-bg: var(--navbar-button-bg);
  --navbar-menu-border-color: #e7ebf0;
  --navbar-menu-link: var(--text);
  --navbar-menu-link-hover: #2c343f;
  --navbar-menu-link-hover-bg: #f4f6fb;
  --navbar-box-shadow: var(--box-shadow);

  // Links
  --link: #3b82f6;
  --link-hover: #2563eb;

  // Spinner
  --spinner-color: var(--link);

  // Modal
  --modal-icon: var(--text);
  --wa-color-overlay-modal: rgba(0, 0, 0, 0.6);

  // Table
  --table-header-bg: var(--bg-2);
  --table-header-text: var(--title-text);
  --table-header-border: var(--bg-4);
  --table-header-seperator: var(--bg-3);
  --table-row-bg: var(--bg);
  --table-row-bg-alt: var(--bg);
  --table-row-border: var(--bg-2);

  // Select
  --select-height: 2.5rem;
  --select-placeholder: var(--form-field-placeholder);
  --select-text: var(--form-input-text);
  --select-bg: var(--form-input-bg);
  --select-option-bg: var(--bg-2);
  --select-option-hover-bg: var(--bg-3);
  --select-option-selected: var(--bg);
  --select-option-selected-bg: var(--bg-5);
  --select-option-disabled: var(--text-white);
  --select-option-disabled-bg: var(--bg-6);
  --select-multi-value-border: var(--bg-4);
  --select-multi-value-bg: var(--bg-3);
  --select-border-width: var(--form-input-border-width);
  --select-border-style: var(--form-input-border-style);
  --select-border-color: var(--form-input-border-color);
  --select-border-radius: var(--form-input-border-radius);
  --select-icon: var(--form-input-text);

  // Form
  --form-bg: var(--bg);
  --form-field-error-text: var(--red);
  --form-field-required: var(--red);
  --form-field-placeholder: var(--bg-5);
  --form-input-bg: var(--bg);
  --form-input-text: var(--text);
  --form-input-border-color: var(--bg-3);
  --form-checkbox-bg: var(--body-bg);
  --form-textarea-preview-bg: var(--bg-1);
  --form-checkbox-unchecked: #fff;
  --form-checkbox-unchecked-border: var(--bg-5);
  --form-checkbox-checked: var(--bg-7);
  --form-checkbox-checked-border: var(--bg-7);

  // Data-manager.
  // Select
  --data-manager-select-bg: var(--bg-2);
  --data-manager-select-text: #313a46;
  --data-manager-select-box-shadow: none;
  --data-manager-select-border-width: 1px;
  --data-manager-select-border-radius: var(--btn-border-radius);
  --data-manager-select-border-color: var(--bg-3);
  --data-manager-select-menu-border-width: 1px;
  --data-manager-select-menu-border-style: solid;
  --data-manager-select-menu-border-color: var(--bg-3);
  --data-manager-select-option: #6c757d;
  --data-manager-select-option-bg: #fff;
  --data-manager-select-option-hover: #2c343f;
  --data-manager-select-option-hover-bg: #f4f6fb;
  --data-manager-select-option-selected: #313a46;
  --data-manager-select-option-selected-bg: #f2f5f9;
  --data-manager-select-multi-value-border: var(--form-select-multi-value-border);

  // Toggle buttons.
  --data-manager-toggle-button-bg: transparant;
  --data-manager-toggle-button-text: var(--text);

  // Footer.
  --data-manager-footer-text: var(--link);
  --data-manager-footer-bg: transparant;
  --data-manager-footer-active-text: var(--text-white);
  --data-manager-footer-active-bg: var(--link);

  // Label list.
  --label-list-header: var(--bg-3);
  --label-list-title: var(--title-text);

  // Badges.
  --role-badge-bg: var(--bg-2);
  --action-hover-bg: var(--bg-2);

  // Dropdown.
  --dropdown-bg-hover: var(--bg-1);
  --dropdown-bg-hover-alternate: var(--bg-4);
  --dropdown-border-color-hover: var(--bg-3);
  --dropdown-menu-bg: var(--navbar-menu-bg);
  --dropdown-menu-border-color: var(--navbar-menu-border-color);
  --dropdown-menu-item-hover-color: var(--navbar-menu-link-hover);
  --dropdown-menu-item-hover-bg: var(--navbar-menu-link-hover-bg);

  // Dropdown accents
  --dropdown-regular: var(--text);
  --dropdown-regular-hover: var(--dropdown-menu-item-hover-color);
  --dropdown-info: var(--link);
  --dropdown-info-hover: var(--link-hover);
  --dropdown-warning: ${orange};
  --dropdown-warning-hover: ${lighten(0.05, orange)};
  --dropdown-error: var(--error);
  --dropdown-error-hover: ${lighten(0.05, red)};
  --dropdown-success: ${green};
  --dropdown-success-hover: ${lighten(0.05, green)};

  // Highlight
  --highlight-color: #edf2fa;

  // Message
  --message-header-bg: var(--bg-3);
  --message-border-left-outgoing: var(--green);
  --message-border-left-outgoing-hover: ${darken(0.05, green)};
  --message-border-left-incoming: var(--link);

  // Alerts
  --alert-regular-bg: var(--body-bg);
  --alert-regular-border: var(--text);

  --alert-info-bg: ${lighten(0.44, blue)};
  --alert-info-border: ${blue};

  --alert-warning-bg: ${lighten(0.44, orange)};
  --alert-warning-border: ${orange};

  // Search highlight.
  --mark-bg: ${lighten(0.38, orange)};
  --mark-text: var(--title-text);

  --alert-error-bg: ${lighten(0.5, red)};
  --alert-error-border: ${red};

  --alert-success-bg: ${lighten(0.5, green)};
  --alert-success-border: ${green};

  // Date picker
  --datepicker-day-selected-bg: var(--link-hover);
  --datepicker-day-selected-bg-middle: var(--link);

  // Tab group
  --tab-group-border: var(--bg-3);
  --tab-group-color: var(--title-text);
  --tab-group-active-color: var(--link);
  --tab-group-hover-color: var(--link-hover);

  // Dashboard
  --tile-button-bg: var(--bg-2);
  --tile-button-bg-hover: var(--bg-3);

  // Action list
  --action-list-row-bg: var(--bg);
  --action-list-row-bg-alt: var(--bg-2);
  --action-sub-action-border: none;

  // Toggle
  --toggle-bg: var(--bg-4);

  // Search
  --search-row-hover-border: var(--bg-5);
`;
